import { useState, useEffect } from "react";
import { detectFileType } from "@/lib/fileTypeUtils";

interface Attachment {
  url: string;
  filename: string;
  contentType?: string;
  size?: number;
}

interface AttachmentPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  attachments: Attachment[];
  initialIndex?: number;
}

export function AttachmentPreviewModal({ 
  isOpen, 
  onClose, 
  attachments, 
  initialIndex = 0 
}: AttachmentPreviewModalProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // Reset index when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
    }
  }, [isOpen, initialIndex]);

  if (!isOpen || attachments.length === 0) return null;

  const current = attachments[currentIndex];
  const hasMultiple = attachments.length > 1;
  
  // Détecter le type de fichier (robuste : contentType + fallback extension)
  const { isImage, isPdf } = detectFileType(current.filename, current.contentType);

  // Formater la taille du fichier
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return null;
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 truncate">
              {current.filename}
            </h2>
            {hasMultiple && (
              <span className="text-sm text-gray-500 whitespace-nowrap">
                ({currentIndex + 1} / {attachments.length})
              </span>
            )}
            {current.size && (
              <span className="text-xs text-gray-400 whitespace-nowrap">
                {formatFileSize(current.size)}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="ml-4 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
            title="Fermer"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Navigation arrows (if multiple attachments) */}
        {hasMultiple && (
          <div className="flex items-center justify-center gap-4 p-2 bg-gray-50 border-b border-gray-200">
            <button
              onClick={() => setCurrentIndex((prev) => Math.max(0, prev - 1))}
              disabled={currentIndex === 0}
              className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              ← Précédent
            </button>
            <span className="text-sm text-gray-600">
              Fichier {currentIndex + 1} sur {attachments.length}
            </span>
            <button
              onClick={() => setCurrentIndex((prev) => Math.min(attachments.length - 1, prev + 1))}
              disabled={currentIndex === attachments.length - 1}
              className="px-3 py-1 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Suivant →
            </button>
          </div>
        )}

        {/* Content Viewer */}
        <div className="flex-1 overflow-auto bg-gray-100 flex items-center justify-center">
          {isImage ? (
            <img
              src={current.url}
              alt={current.filename}
              className="max-w-full max-h-full object-contain"
            />
          ) : isPdf ? (
            <iframe
              src={current.url}
              className="w-full h-full border-0"
              title={`PDF Preview: ${current.filename}`}
            />
          ) : (
            <div className="text-center p-8">
              <div className="text-6xl mb-4">📎</div>
              <p className="text-gray-600 mb-4">
                Aperçu non disponible pour ce type de fichier
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Type : {current.contentType || 'inconnu'}
              </p>
              <a
                href={current.url}
                download={current.filename}
                className="inline-block px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 transition-colors"
              >
                📥 Télécharger le fichier
              </a>
            </div>
          )}
        </div>

        {/* Footer with actions */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center gap-3">
            <a
              href={current.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
            >
              🔗 Ouvrir dans un nouvel onglet
            </a>
            <a
              href={current.url}
              download={current.filename}
              className="text-sm text-gray-600 hover:text-gray-800 font-medium transition-colors"
            >
              📥 Télécharger
            </a>
          </div>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
